// utils/guestFormatter.ts

export const formatGuests = (
  adultCount: number,
  teenagerCount: number,
  childGuests: number,
  infantGuests: number,
  t: (key: string, options?: Record<string, string | number>) => string,
) => {
  const parts = [];
  if (adultCount > 0) {
    parts.push(t("header.adult", { count: adultCount }));
  }
  if (teenagerCount > 0) {
    parts.push(t("header.teenager", { count: teenagerCount }));
  }
  if (childGuests > 0) {
    parts.push(t("header.child", { count: childGuests }));
  }
  if (infantGuests > 0) {
    parts.push(t("header.infant", { count: infantGuests }));
  }
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return (
    parts.slice(0, -1).join(", ") +
    " " +
    t("common.and") +
    " " +
    parts[parts.length - 1]
  );
};

export const getGuestCountSummary = (
  guests: {
    adults: number;
    teenagers: number;
    children: number;
    infants: number;
  },
  t: (key: string, options?: Record<string, string | number>) => string,
): string => {
  const parts = [];
  if (guests.adults)
    parts.push(
      `${guests.adults} ${t("guest_types.adults", { count: guests.adults })}`,
    );
  if (guests.teenagers)
    parts.push(
      `${guests.teenagers} ${t("guest_types.teenagers", { count: guests.teenagers })}`,
    );
  if (guests.children)
    parts.push(
      `${guests.children} ${t("guest_types.children", { count: guests.children })}`,
    );
  if (guests.infants)
    parts.push(
      `${guests.infants} ${t("guest_types.infants", { count: guests.infants })}`,
    );
  return parts.length > 0 ? parts.join(", ") : t("checkout.no_guests");
};

/** Guest line for a reservation row. A whole-activity row is not a guest mix —
 *  the party size is asked for as one number and every guest sits in the adult
 *  slot, so a per-type count would read "20 vuxna" for a family. Naming the row
 *  and appending the headcount matches the mail, and explains what a per-person
 *  product ad on the row multiplied by. */
export const getRowGuestSummary = (
  guests: {
    adults: number;
    teenagers: number;
    children: number;
    infants: number;
  },
  bookEntireOccurrence: boolean | undefined,
  t: (key: string, options?: Record<string, string | number>) => string,
): string => {
  if (!bookEntireOccurrence) return getGuestCountSummary(guests, t);

  const participants =
    guests.adults + guests.teenagers + guests.children + guests.infants;
  return participants > 0
    ? t("booking.activity_entire_line_with_count", { count: participants })
    : t("booking.activity_entire_line");
};
